/**
 * Visitor-analytics consent — the gate backend#411 requires before any
 * per-visitor session tracking runs on a public page.
 *
 * The Privacy Doctrine amendment (manticscore-backend#411) allows anonymous
 * session-level analytics on public clip pages: a product-minted session id,
 * country, device class and referrer domain — never a raw IP, raw user agent
 * or full referrer URL — expiring after 90 days. GDPR/CCPA apply to visitors
 * who never signed up, so consent must be collected BEFORE any of it runs.
 *
 * This module is that gate. No browser SDK for `POST /api/public/events`
 * exists in this repo yet; when one lands it MUST check
 * `visitorAnalyticsAllowed()` before sending any event or minting/reading an
 * analytics session id. The contract:
 *
 * - Storage: `localStorage["mc_visitor_analytics_consent"]`.
 * - Values: `"granted"` or `"denied"`. Anything else — including absence —
 *   means the visitor has not decided, and NOT DECIDED MEANS NO TRACKING.
 * - Decline is sticky: a stored `"denied"` keeps the banner away and the
 *   SDK off until the visitor changes it themselves.
 * - Storage that throws (private mode, blocked) reads as undecided, which
 *   fails toward no tracking. The banner may re-show; events still never send.
 *
 * Deliberately NOT gated here: the server-minted `mc_anon` cookie
 * (view-to-click correlation on `GET /clip/{slug}`, httponly — predates the
 * amendment and is scoped to that one purpose) and PostHog product analytics
 * in `src/lib/analytics.ts`. This gate covers the per-visitor analytics
 * layer the amendment introduced, nothing else.
 */

export const VISITOR_ANALYTICS_CONSENT_KEY = "mc_visitor_analytics_consent";

export type VisitorAnalyticsConsent = "granted" | "denied";

/** The stored decision, or null when the visitor has not decided. */
export function getVisitorAnalyticsConsent(): VisitorAnalyticsConsent | null {
  try {
    const value = window.localStorage.getItem(VISITOR_ANALYTICS_CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function setVisitorAnalyticsConsent(value: VisitorAnalyticsConsent): void {
  try {
    window.localStorage.setItem(VISITOR_ANALYTICS_CONSENT_KEY, value);
  } catch {
    // Storage is an optional enhancement; an unstorable decision simply
    // means the banner asks again. Tracking still requires a readable grant.
  }
}

/**
 * The single admission test the future events SDK must call. Strict:
 * only an explicit stored `"granted"` allows tracking.
 */
export function visitorAnalyticsAllowed(): boolean {
  return getVisitorAnalyticsConsent() === "granted";
}

/**
 * Routes where per-visitor analytics would run, and therefore where the
 * consent banner shows: `/clip` covers the clip page and everything under it
 * (live turns, stats, and since #237 the creator page at `/clip/{handle}`),
 * `/spotlight` the gallery. The authed owner surfaces, the landing page and
 * the legal pages are deliberately outside it.
 *
 * `/u/` stays in the list even though it 308s to `/clip/{handle}` at the edge.
 * An in-app navigation to the deprecated path never reaches the edge, and the
 * client redirect renders a frame first — a banner that flickers off for that
 * frame is worse than one that was always on.
 */
export function isConsentSurface(pathname: string): boolean {
  return (
    pathname === "/clip" ||
    pathname.startsWith("/clip/") ||
    pathname === "/spotlight" ||
    pathname.startsWith("/u/")
  );
}
